import { useState, useRef, useEffect, useCallback } from 'react'
import { useDepartment } from '../../context/DepartmentContext'
import { useToast } from '../../context/ToastContext'
import { fuzzyMatch, getProducts } from '../../data/products'
import { getProductPrice } from '../../lib/productPricing'
import { parsePosLine } from '../../lib/pdfPosExtract'
import { lookupProductMapping, saveProductMapping } from '../../lib/productMappings'
import { getCategories } from '../../data/categories'
import {
  saveSalesBatch, saveFlagsBatch, saveMorningLog, saveSurplusEntry,
  saveProductionBatch, saveDistributionBatch, saveDamagesBatch, saveBakerySurplusBatch,
  saveExpenditureBatch, saveCashOnHand,
  getSalesTransactions, deleteSalesForDate, deleteSaleTransaction, verifySalesForDate,
} from '../../lib/store'
import { generateTransactionFlags } from '../../lib/flagging'
import { calculateExpectedSales, formatR } from '../../lib/calculations'
import ArithmeticInput from '../ui/ArithmeticInput'
import {
  Upload, FileCheck, AlertCircle, Check, Trash2, RefreshCw, Pencil,
  Save, X, Loader, FileSpreadsheet, FileText, Eye, Edit3
} from 'lucide-react'
import AddProductModal from './AddProductModal'

// ─── PDF text extraction with timeout ───
async function extractPdfText(file, onProgress) {
  onProgress?.('Loading PDF library...')
  const { configurePdfWorker, pdfjsLib } = await import('../../lib/pdfWorker')
  configurePdfWorker()

  onProgress?.('Reading file...')
  const arrayBuffer = await file.arrayBuffer()

  onProgress?.('Parsing PDF structure...')
  const loadPromise = pdfjsLib.getDocument(arrayBuffer).promise
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('PDF loading timed out after 60 seconds. Try a smaller file.')), 60000)
  )
  const pdf = await Promise.race([loadPromise, timeoutPromise])

  let fullText = ''
  for (let i = 1; i <= pdf.numPages; i++) {
    onProgress?.(`Extracting text from page ${i} of ${pdf.numPages}...`)
    try {
      const page = await pdf.getPage(i)
      const content = await page.getTextContent()
      // ─── Diagnostic logging ─────────────────────────────────────────
      // Surface what pdf.js is actually extracting from the source PDF
      // before any of our reconstruction logic touches it. The most
      // likely iOS failure mode is items.length === 0 (glyph-encoded
      // text). Other modes show up as collapsed Y coordinates or
      // empty/garbled item.str values.
      console.log(`[PDF DEBUG] Page ${i}/${pdf.numPages} — items: ${content.items.length}`)
      if (content.items.length > 0) {
        console.log('[PDF DEBUG] First 10 item.str values:',
          content.items.slice(0, 10).map(it => JSON.stringify(it.str)))
        console.log('[PDF DEBUG] First 10 Y coords:',
          content.items.slice(0, 10).map(it => it.transform?.[5]))
      } else {
        console.warn(`[PDF DEBUG] Page ${i} has ZERO text items — likely glyph-encoded`)
      }
      // Reconstruct tabular layout using position data
      const items = content.items.filter(item => item.str.trim())
      if (items.length === 0) continue
      // Group by Y coordinate (with tolerance for slight misalignment)
      const rows = []
      let currentRow = []
      let lastY = null
      const sorted = [...items].sort((a, b) => {
        const yDiff = b.transform[5] - a.transform[5]
        if (Math.abs(yDiff) > 3) return yDiff
        return a.transform[4] - b.transform[4]
      })
      for (const item of sorted) {
        const y = Math.round(item.transform[5])
        if (lastY !== null && Math.abs(y - lastY) > 3) {
          if (currentRow.length > 0) rows.push(currentRow)
          currentRow = []
        }
        currentRow.push(item)
        lastY = y
      }
      if (currentRow.length > 0) rows.push(currentRow)
      // Build text with multi-space column separators
      for (const row of rows) {
        row.sort((a, b) => a.transform[4] - b.transform[4])
        let lineText = ''
        let lastX = 0
        for (const item of row) {
          const x = item.transform[4]
          const gap = x - lastX
          if (lineText && gap > 20) {
            lineText += '   '
          } else if (lineText && gap > 5) {
            lineText += ' '
          }
          lineText += item.str
          lastX = x + (item.width || item.str.length * 5)
        }
        fullText += lineText + '\n'
      }
    } catch (err) {
      console.warn(`Failed to parse page ${i}:`, err)
      fullText += `\n[Page ${i} failed to parse]\n`
    }
  }
  // ─── Diagnostic logging ───────────────────────────────────────────
  // Final synthesised text the parser will consume. If this is empty
  // or short despite items.length > 0 above, the position-grouping
  // logic collapsed everything into nothing usable.
  console.log('[PDF DEBUG] Final fullText length:', fullText.length)
  console.log('[PDF DEBUG] First 500 chars:', fullText.substring(0, 500))
  console.log('[PDF DEBUG] Line count:', fullText.split('\n').length)
  return fullText
}

// PDF sales parser uses the canonical parsePosLine from
// src/lib/pdfPosExtract.js — see imports at top of file. The local
// copy has been removed so future fixes flow from a single source.

// Parse all transactions from the PDF text. Section filtering has
// been removed — items from any POS section (Kitchen, Bakery, Soft
// Drink, Not Grouped, etc.) are returned. The matching pipeline
// downstream uses fuzzyMatch + saved product mappings to decide
// which items belong to the current department, so a kitchen item
// miscategorised under "Soft Drink" in the POS still gets imported.
function parseSalesSection(text /* department arg unused */) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
  const rows = []
  let dateStr = null

  const fullMatch = text.match(/For The Period\s+(\d+)\/(\d+)\/(\d{4})/i)
  if (fullMatch) {
    const month = fullMatch[1].padStart(2, '0')
    const dayPart = fullMatch[2].padStart(2, '0')
    dateStr = `${fullMatch[3]}-${month}-${dayPart}`
  }
  if (!dateStr) {
    const dateMatch = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/)
    if (dateMatch) {
      dateStr = `${dateMatch[3]}-${dateMatch[2].padStart(2, '0')}-${dateMatch[1].padStart(2, '0')}`
    }
  }

  for (const line of lines) {
    const parsed = parsePosLine(line)
    if (parsed) {
      rows.push({ rawName: parsed.name, productCode: parsed.productCode, qty: parsed.qty, sales: parsed.sales })
    }
  }

  return { date: dateStr, rows }
}

// ─── Excel parser ───
async function parseExcelFile(file, department, dataType) {
  const XLSX = await import('xlsx')
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true })

  const rows = []
  let detectedDate = null

  for (const sheetName of workbook.SheetNames) {
    const ws = workbook.Sheets[sheetName]
    const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false })
    if (json.length < 2) continue

    // Find header row
    const keywords = ['date', 'product', 'item', 'qty', 'quantity', 'sales', 'amount', 'name', 'code', 'total', 'price']
    let headerIdx = 0
    for (let i = 0; i < Math.min(json.length, 6); i++) {
      const row = (json[i] || []).map(c => String(c).toLowerCase().trim())
      if (row.filter(c => keywords.some(k => c.includes(k))).length >= 2) { headerIdx = i; break }
    }

    const dataRows = json.slice(headerIdx + 1).filter(row => row.some(c => c !== '' && c !== null && c !== undefined))

    for (const row of dataRows) {
      // Try to detect date from first column
      const dateVal = row[0]
      if (dateVal && !detectedDate) {
        const parsed = parseSimpleDate(dateVal)
        if (parsed) detectedDate = parsed
      }

      const nameOrCode = String(row[1] || row[0] || '').trim()
      if (!nameOrCode) continue
      const qty = parseFloat(String(row[2] || row[1] || '0').replace(/[^0-9.]/g, ''))
      const sales = parseFloat(String(row[3] || row[2] || '0').replace(/[^0-9.]/g, ''))

      if (!isNaN(qty) || !isNaN(sales)) {
        rows.push({ rawName: nameOrCode, qty: qty || 0, sales: sales || 0 })
      }
    }
  }

  return { date: detectedDate, rows }
}

function parseSimpleDate(val) {
  if (!val) return null
  if (typeof val === 'number') {
    const d = new Date((val - 25569) * 86400 * 1000)
    return d.toISOString().split('T')[0]
  }
  const str = String(val).trim()
  const dmy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  return null
}

// ─── Review states ───
const REVIEW_STATES = { IDLE: 'idle', PARSING: 'parsing', REVIEW: 'review', CONFIRMED: 'confirmed', SAVED: 'saved' }

export default function SingleDayUpload({ dataType, date, onSaved }) {
  const { currentDepartmentId } = useDepartment()
  const { addToast } = useToast()
  const fileRef = useRef()
  const department = currentDepartmentId

  const [state, setState] = useState(REVIEW_STATES.IDLE)
  const [parseProgress, setParseProgress] = useState('')
  const [parseError, setParseError] = useState(null)
  const [uploadedFile, setUploadedFile] = useState(null) // { name, type, raw: File }
  const [parsedRows, setParsedRows] = useState([])
  const [detectedDate, setDetectedDate] = useState(null)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [addProductTarget, setAddProductTarget] = useState(null)

  // Saved data view state
  const [savedData, setSavedData] = useState([])
  const [viewMode, setViewMode] = useState(false) // true = showing saved data

  const products = getProducts(department)

  // Load existing data when date/department changes
  const loadSavedData = useCallback(() => {
    const txns = getSalesTransactions(department, date)
    if (txns.length > 0) {
      setSavedData(txns)
      setViewMode(true)
      setState(REVIEW_STATES.IDLE)
    } else {
      setSavedData([])
      setViewMode(false)
    }
  }, [department, date])

  useEffect(() => { loadSavedData() }, [loadSavedData])

  // Re-read from localStorage when background sync updates data
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.department === department && e.detail?.date === date) loadSavedData()
    }
    window.addEventListener('desres-sync', handler)
    return () => window.removeEventListener('desres-sync', handler)
  }, [department, date, loadSavedData])

  const handleDeleteSaved = async () => {
    if (!confirm(`Delete all ${dataType} entries for ${date}?`)) return
    const count = await deleteSalesForDate(department, date, 'excel')
    addToast(`Deleted ${count} uploaded entries`, 'success')
    setSavedData([])
    setViewMode(false)
    onSaved?.()
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const isPdf = file.name.toLowerCase().endsWith('.pdf')
    const isExcel = /\.(xlsx?|xls)$/i.test(file.name)

    if (!isPdf && !isExcel) {
      addToast('Please upload an Excel (.xlsx, .xls) or PDF file', 'error')
      return
    }

    setUploadedFile({ name: file.name, type: isPdf ? 'pdf' : 'excel', raw: file })
    setState(REVIEW_STATES.PARSING)
    setParseProgress(isPdf ? 'Loading PDF...' : 'Reading Excel file...')
    setParseError(null)

    try {
      let result
      if (isPdf) {
        const text = await extractPdfText(file, setParseProgress)
        result = parseSalesSection(text, department)
      } else {
        result = await parseExcelFile(file, department, dataType)
      }

      if (result.date) setDetectedDate(result.date)

      // Match rows to products. Match priority:
      //   1. Saved manual mapping (user previously linked this raw name)
      //   2. Exact productCode match (case-insensitive)
      //   3. fuzzyMatch on the full name
      const allProducts = getProducts(department)
      const matched = result.rows.map(row => {
        // 1. Saved mapping
        const mappedCode = lookupProductMapping(department, row.rawName)
        if (mappedCode) {
          const product = allProducts.find(p => p.code?.toLowerCase() === mappedCode.toLowerCase())
          if (product) {
            return {
              ...row, matched: product, confidence: 100,
              status: 'matched', selectedCode: product.code, editing: false,
            }
          }
        }

        // 2. Exact productCode match against the parsed leading token
        if (row.productCode) {
          const product = allProducts.find(p => p.code?.toLowerCase() === row.productCode.toLowerCase())
          if (product) {
            return {
              ...row, matched: product, confidence: 100,
              status: 'matched', selectedCode: product.code, editing: false,
            }
          }
        }

        // 3. Fuzzy fallback
        const match = fuzzyMatch(row.rawName, department)
        return {
          ...row,
          matched: match?.product || null,
          confidence: match?.confidence || 0,
          status: match
            ? match.confidence >= 85 ? 'matched' : 'fuzzy'
            : 'unknown',
          selectedCode: match?.product?.code || '',
          editing: false,
        }
      })

      if (matched.length === 0) {
        setParseError('No data rows found in the file. Make sure the file contains sales/entry data.')
        setState(REVIEW_STATES.IDLE)
        return
      }

      setParsedRows(matched)
      setState(REVIEW_STATES.REVIEW)
      setParseProgress('')

      if (result.date && result.date !== date) {
        addToast(`Document date detected: ${result.date} (current: ${date})`, 'info')
      }
    } catch (err) {
      console.error('Parse error:', err)
      setParseError(err.message || 'Failed to parse file')
      setState(REVIEW_STATES.IDLE)
      setParseProgress('')
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const updateRow = (idx, field, value) => {
    setParsedRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }

  const removeRow = (idx) => {
    setParsedRows(prev => prev.filter((_, i) => i !== idx))
  }

  const updateMatch = (idx, code) => {
    if (code === '__NEW__') {
      setAddProductTarget(idx)
      setShowAddProduct(true)
      return
    }
    const product = products.find(p => p.code === code)
    // Persist the manual mapping so the same raw POS name auto-resolves
    // to this product on future uploads (per-department).
    const targetRow = parsedRows[idx]
    if (targetRow?.rawName) {
      saveProductMapping(department, targetRow.rawName, code || null)
    }
    setParsedRows(prev => prev.map((r, i) =>
      i === idx ? { ...r, matched: product || null, selectedCode: code, status: product ? 'matched' : 'unknown', confidence: product ? 100 : 0 } : r
    ))
  }

  const handleNewProductAdded = (product) => {
    if (addProductTarget !== null) {
      setParsedRows(prev => prev.map((r, i) =>
        i === addProductTarget ? { ...r, matched: product, selectedCode: product.code, status: 'matched', confidence: 100 } : r
      ))
    }
  }

  const confirmData = () => {
    setState(REVIEW_STATES.CONFIRMED)
  }

  const saveData = async () => {
    const useDate = date // always use the selected day's date
    let count = 0

    // Sales-type save
    const transactions = parsedRows
      .filter(row => row.matched)
      .map(row => ({
        date: useDate,
        productCode: row.matched.code,
        productName: row.matched.name,
        category: row.matched.category,
        qtySold: row.qty,
        salesRands: row.sales,
        unitPrice: getProductPrice(row.matched, currentDepartmentId) ?? row.matched.unitPrice,
        qps: row.matched.qps,
        saleType: row.matched.saleType,
        totalSalesExpected: (() => {
          const px = getProductPrice(row.matched, currentDepartmentId)
          return px ? px * row.qty : null
        })(),
        difference: (() => {
          const px = getProductPrice(row.matched, currentDepartmentId)
          return px ? row.sales - (px * row.qty) : null
        })(),
        entryMode: uploadedFile?.type || 'upload',
        sourceFile: uploadedFile?.name,
      }))

    // Pre-aggregate duplicate product codes so count matches unique products
    const aggregated = []
    const byCode = {}
    for (const tx of transactions) {
      if (byCode[tx.productCode]) {
        byCode[tx.productCode].qtySold += tx.qtySold
        byCode[tx.productCode].salesRands += tx.salesRands
      } else {
        const entry = { ...tx }
        byCode[tx.productCode] = entry
        aggregated.push(entry)
      }
    }

    if (aggregated.length > 0) {
      // Delete ALL existing sales for this date first to prevent accumulation
      await deleteSalesForDate(department, useDate)

      const results = saveSalesBatch(department, aggregated)
      count = results.length
      const allFlags = []
      for (const { record } of results) {
        const flags = generateTransactionFlags(record)
        allFlags.push(...flags)
      }
      if (allFlags.length > 0) saveFlagsBatch(department, allFlags)
    }

    addToast(`Saved ${count} entries for ${useDate}`, count > 0 ? 'success' : 'warning')

    // Post-save verification
    verifySalesForDate(department, useDate).then(result => {
      if (!result.ok) {
        console.warn(`[VERIFY] ${useDate}:`, result.warnings)
        addToast(`Warning: data integrity issue — check console`, 'warning')
      }
    })

    setState(REVIEW_STATES.IDLE)
    loadSavedData()
    onSaved?.()
  }

  const resetUpload = () => {
    setState(REVIEW_STATES.IDLE)
    setParsedRows([])
    setUploadedFile(null)
    setDetectedDate(null)
    setParseError(null)
    setParseProgress('')
  }

  const reUpload = () => {
    resetUpload()
    setTimeout(() => fileRef.current?.click(), 100)
  }

  const rowColor = (status) => {
    switch (status) {
      case 'matched': return 'bg-green-950/30'
      case 'fuzzy': return 'bg-amber-950/30'
      case 'unknown': return 'bg-red-950/30'
      default: return ''
    }
  }

  const matchedCount = parsedRows.filter(r => r.status === 'matched' || r.status === 'fuzzy').length
  const unknownCount = parsedRows.filter(r => r.status === 'unknown').length

  return (
    <div className="space-y-4">
      {/* Saved data view */}
      {viewMode && savedData.length > 0 && state === REVIEW_STATES.IDLE && (() => {
        const totalSales = savedData.reduce((s, r) => s + (Number(r.salesRands) || 0), 0)
        const totalQty = savedData.reduce((s, r) => s + (Number(r.qtySold) || 0), 0)
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <Eye size={16} /> Saved Sales — {savedData.length} items, {formatR(totalSales)} total
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setViewMode(false) }}
                  className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs"
                >
                  <Upload size={12} /> Upload New
                </button>
                <button
                  onClick={handleDeleteSaved}
                  className="flex items-center gap-1 bg-gray-800 hover:bg-red-900/50 text-red-400 px-3 py-1.5 rounded text-xs"
                >
                  <Trash2 size={12} /> Delete All
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-xs border-b border-gray-800">
                    <th className="text-left py-2 px-2">Product</th>
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2">Category</th>
                    <th className="text-right py-2 px-2">Qty</th>
                    <th className="text-right py-2 px-2">Sales (R)</th>
                    <th className="text-right py-2 px-2">Expected</th>
                    <th className="text-right py-2 px-2">Diff</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {savedData.map(row => {
                    const expected = calculateExpectedSales(row.unitPrice, Number(row.qtySold))
                    const diff = expected && row.salesRands ? Number(row.salesRands) - expected : null
                    return (
                      <tr key={row.id} className={`border-b border-gray-800/50 ${diff && diff !== 0 ? 'bg-amber-950/20' : ''}`}>
                        <td className="py-2 px-2 font-mono text-xs text-gray-400">{row.productCode}</td>
                        <td className="py-2 px-2 text-sm">{row.productName || '\u2014'}</td>
                        <td className="py-2 px-2 text-xs text-gray-400 capitalize">{row.category || '\u2014'}</td>
                        <td className="py-2 px-2 text-right">{row.qtySold || '\u2014'}</td>
                        <td className="py-2 px-2 text-right font-medium">{formatR(row.salesRands)}</td>
                        <td className="py-2 px-2 text-right text-xs text-gray-400">{expected !== null ? formatR(expected) : '\u2014'}</td>
                        <td className={`py-2 px-2 text-right text-xs font-medium ${
                          diff > 0 ? 'text-green-400' : diff < 0 ? 'text-red-400' : 'text-gray-400'
                        }`}>
                          {diff !== null ? `${diff >= 0 ? '+' : ''}${formatR(diff)}` : '\u2014'}
                        </td>
                        <td className="py-2 px-2">
                          <button
                            onClick={() => {
                              if (!confirm(`Delete ${row.productName || row.productCode}?`)) return
                              deleteSaleTransaction(row.id)
                              loadSavedData()
                              addToast('Entry deleted', 'success')
                              onSaved?.()
                            }}
                            className="text-gray-600 hover:text-red-400"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-700 text-sm font-medium">
                    <td colSpan="3" className="py-2 px-2 text-gray-400">Total</td>
                    <td className="py-2 px-2 text-right">{totalQty}</td>
                    <td className="py-2 px-2 text-right text-white">{formatR(totalSales)}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )
      })()}

      {/* Upload area */}
      {state === REVIEW_STATES.IDLE && !viewMode && (
        <>
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-700 rounded-lg p-12 text-center cursor-pointer hover:border-gray-500 transition-colors"
          >
            <Upload size={32} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Drop a file here or click to upload</p>
            <p className="text-gray-600 text-xs mt-1">
              <FileSpreadsheet size={12} className="inline mr-1" />Excel (.xlsx, .xls) or
              <FileText size={12} className="inline mx-1" />PDF for <strong>{date}</strong>
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.pdf,application/pdf,application/x-pdf"
              onChange={handleFile}
              style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
            />
          </div>

          {parseError && (
            <div className="bg-red-950/30 border border-red-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-300">{parseError}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="text-xs bg-red-800 hover:bg-red-700 text-white px-3 py-1.5 rounded flex items-center gap-1"
                  >
                    <RefreshCw size={12} /> Try Another File
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Parsing progress */}
      {state === REVIEW_STATES.PARSING && (
        <div className="text-center py-12 space-y-3">
          <Loader size={28} className="text-brand-400 mx-auto animate-spin" />
          <p className="text-gray-400 text-sm">{parseProgress || 'Processing...'}</p>
          <p className="text-gray-600 text-xs">File: {uploadedFile?.name}</p>
        </div>
      )}

      {/* Review parsed data */}
      {(state === REVIEW_STATES.REVIEW || state === REVIEW_STATES.CONFIRMED) && (
        <div className="space-y-4">
          {/* File info bar */}
          <div className="bg-brand-950/30 border border-brand-800 rounded-lg p-4 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <FileCheck size={18} className="text-brand-400" />
              <div>
                <p className="text-sm text-white font-medium">{uploadedFile?.name}</p>
                <p className="text-xs text-gray-500">
                  {parsedRows.length} items found — {matchedCount} matched
                  {unknownCount > 0 && <span className="text-amber-400 ml-1">({unknownCount} unmatched)</span>}
                  {detectedDate && detectedDate !== date && (
                    <span className="text-blue-400 ml-2">Document date: {detectedDate}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={reUpload} className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded flex items-center gap-1">
                <RefreshCw size={12} /> Re-upload
              </button>
              <button onClick={resetUpload} className="text-xs bg-gray-700 hover:bg-gray-600 text-red-300 px-3 py-1.5 rounded flex items-center gap-1">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>

          {/* Existing data warning */}
          {savedData.length > 0 && (
            <div className="bg-amber-950/40 border border-amber-800 rounded-lg px-4 py-3 flex items-center gap-3">
              <AlertCircle size={16} className="text-amber-400 shrink-0" />
              <p className="text-xs text-amber-300">
                Sales data already exists for this date ({savedData.length} items, R{savedData.reduce((s, r) => s + (Number(r.salesRands) || 0), 0).toFixed(0)}).
                Saving will <strong>replace</strong> all existing records.
              </p>
            </div>
          )}

          {/* Match legend */}
          <div className="flex gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-800" /> Matched</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-800" /> Fuzzy</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-800" /> Unknown</span>
          </div>

          {/* Data table */}
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-900">
                <tr className="text-gray-400 text-xs border-b border-gray-800">
                  <th className="text-left py-2 px-2">Raw Name</th>
                  <th className="text-left py-2 px-2">Match</th>
                  <th className="text-right py-2 px-2">Qty</th>
                  <th className="text-right py-2 px-2">Sales (R)</th>
                  <th className="text-center py-2 px-2">Conf</th>
                  <th className="py-2 px-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, ri) => (
                  <tr key={ri} className={`border-b border-gray-800/50 ${rowColor(row.status)}`}>
                    <td className="py-2 px-2 text-gray-300 text-xs">{row.rawName}</td>
                    <td className="py-2 px-2">
                      {row.status === 'unknown' ? (
                        <select
                          value={row.selectedCode}
                          onChange={e => updateMatch(ri, e.target.value)}
                          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
                        >
                          <option value="">Map to product...</option>
                          {products.map(p => (
                            <option key={p.code} value={p.code}>{p.code} — {p.name}</option>
                          ))}
                          <option value="__NEW__">+ Create New Product</option>
                        </select>
                      ) : (
                        <span className="text-xs">{row.matched?.code} — {row.matched?.name}</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      <ArithmeticInput
                        min="0"
                        value={row.qty}
                        onChange={(val) => updateRow(ri, 'qty', val == null ? 0 : Number(val))}
                        disabled={state === REVIEW_STATES.CONFIRMED}
                        className="bg-transparent border border-transparent hover:border-gray-600 focus:border-brand-500 rounded px-1 py-0.5 text-xs text-white w-16 text-right outline-none disabled:opacity-60"
                      />
                    </td>
                    <td className="py-1 px-2">
                      <ArithmeticInput
                        min="0"
                        value={row.sales}
                        onChange={(val) => updateRow(ri, 'sales', val == null ? 0 : Number(val))}
                        disabled={state === REVIEW_STATES.CONFIRMED}
                        className="bg-transparent border border-transparent hover:border-gray-600 focus:border-brand-500 rounded px-1 py-0.5 text-xs text-white w-20 text-right outline-none disabled:opacity-60"
                      />
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className={`text-xs ${row.confidence >= 85 ? 'text-green-400' : row.confidence >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                        {row.confidence}%
                      </span>
                    </td>
                    <td className="py-2 px-1">
                      {state !== REVIEW_STATES.CONFIRMED && (
                        <button onClick={() => removeRow(ri)} className="text-gray-600 hover:text-red-400">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action buttons: Edit | Confirm | Save */}
          <div className="flex gap-3 flex-wrap">
            {state === REVIEW_STATES.REVIEW && (
              <>
                <button
                  onClick={confirmData}
                  className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600 text-white px-5 py-2.5 rounded text-sm font-medium"
                >
                  <Check size={14} /> Confirm Data
                </button>
                <span className="text-xs text-gray-500 self-center">
                  Review and edit above, then confirm
                </span>
              </>
            )}
            {state === REVIEW_STATES.CONFIRMED && (
              <>
                <button
                  onClick={() => setState(REVIEW_STATES.REVIEW)}
                  className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-5 py-2.5 rounded text-sm font-medium"
                >
                  <Pencil size={14} /> Edit
                </button>
                <button
                  onClick={saveData}
                  className="flex items-center gap-2 bg-green-700 hover:bg-green-600 text-white px-5 py-2.5 rounded text-sm font-medium"
                >
                  <Save size={14} /> Save to {date}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Saved success — fallback if no saved data loaded yet */}
      {state === REVIEW_STATES.SAVED && !viewMode && (
        <div className="text-center py-8">
          <Check size={32} className="text-green-400 mx-auto mb-2" />
          <p className="text-green-400 text-sm">Data saved successfully for {date}</p>
          <div className="flex gap-3 justify-center mt-4">
            <button
              onClick={resetUpload}
              className="text-gray-400 text-xs hover:text-white bg-gray-800 px-3 py-1.5 rounded"
            >
              Upload another file
            </button>
          </div>
        </div>
      )}

      {showAddProduct && (
        <AddProductModal
          department={department}
          prefillName={addProductTarget !== null ? parsedRows[addProductTarget]?.rawName : ''}
          onClose={() => { setShowAddProduct(false); setAddProductTarget(null) }}
          onProductAdded={handleNewProductAdded}
        />
      )}
    </div>
  )
}
